/**
 * contact_import_controller.js — the /clients/import review form.
 *
 * Template: templates/clients/import.html (+ clients/_import_list.html, which
 * every preview replaces). The page works without this controller for a .vcf
 * upload; this adds:
 *   - "Choose contacts": the multi-select picker where the device has one
 *     (ChippieBridge.contactPickerMultiple), else the single picker in a loop
 *     ("Add another contact"). Where there is nothing to pick from (any
 *     iPhone browser, desktop) it stays hidden and a line points to the
 *     Chippie app, which has the picker; the .vcf upload remains.
 *   - preview on file choice, Select all, and the ticked count on Import.
 *
 * Rows are never rendered or judged here. Picked contacts go into the hidden
 * `incoming` field and the form is submitted to the preview, whose Turbo
 * Stream re-renders the whole list with the server's verdict on each row.
 *
 * A non-empty list is registered as unsaved work, so leaving the page asks
 * first and page-check never swaps the picked contacts away. Import itself
 * releases it, so the redirect to /clients is not questioned.
 */

import { Controller } from "@hotwired/stimulus";

import { guardUnsavedWork, releaseUnsavedWork, unsavedWorkChanged } from "./_unsaved_work.js";

const ROW_BOXES = 'input[name="selected"]';

export default class extends Controller {
  static targets = ["incoming", "pick", "pickLabel", "appHint", "vcard", "preview", "submit", "submitLabel", "all"];

  connect() {
    this._committing = false;
    const picker = this._hasPicker();
    this.pickTarget.hidden = !picker;
    this.appHintTarget.hidden = picker;  // iPhone browsers have no picker; the app does
    this.previewTarget.hidden = true;  // choosing a file previews it
    guardUnsavedWork(this.element, () => !this._committing && this._rows().length > 0);
    this.count();
  }

  disconnect() {
    releaseUnsavedWork(this.element);
  }

  allTargetConnected() {
    this.count();
    unsavedWorkChanged();
  }

  async pick() {
    const contacts = await this._pickContacts();
    if (contacts.length === 0) return;
    this.incomingTarget.value = JSON.stringify(contacts);
    this.element.requestSubmit(this.previewTarget);
  }

  load() {
    if (this.vcardTarget.files.length) this.element.requestSubmit(this.previewTarget);
  }

  submitStart(event) {
    this._committing = event.detail.formSubmission.submitter !== this.previewTarget;
  }

  submitted(event) {
    if (this._committing) {
      if (!event.detail.success) this._committing = false;
      return;
    }
    this.incomingTarget.value = "";
    this.vcardTarget.value = "";
  }

  toggleAll() {
    this._rows().forEach((box) => { box.checked = this.allTarget.checked; });
    this.count();
  }

  count() {
    const rows = this._rows();
    const ticked = rows.filter((box) => box.checked).length;
    this.submitTarget.disabled = ticked === 0;
    this.submitLabelTarget.textContent = ticked ? `Import ${ticked} selected` : "Import selected";
    if (this.hasAllTarget) this.allTarget.checked = ticked > 0 && ticked === rows.length;
  }

  // Every row box that can be ticked; disabled ones are rows Import would skip.
  _rows() {
    return Array.from(this.element.querySelectorAll(ROW_BOXES)).filter((box) => !box.disabled);
  }

  _hasPicker() {
    const bridge = window.ChippieBridge;
    if (!bridge || typeof bridge.contactPicker !== "function") return false;
    return bridge._platform() !== "pwa" || Boolean(navigator.contacts);
  }

  // Several at once where the device can; otherwise one, and the button
  // becomes "Add another contact" for the next round. [] when cancelled.
  async _pickContacts() {
    const bridge = window.ChippieBridge;
    try {
      if (typeof bridge.contactPickerMultiple === "function") {
        return await bridge.contactPickerMultiple();
      }
    } catch (error) {
      if (error.message !== "contact-unavailable") return [];
    }
    try {
      const contact = await bridge.contactPicker();
      this.pickLabelTarget.textContent = "Add another contact";
      return [contact];
    } catch (_error) {
      return [];
    }
  }
}
